/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import utils from '../../../../common/utils';

export default class GetGraphService extends BaseService {
  constructor() {
    super(constants.GET_GRAPH, 'getGraphStatus');
  }

  getErrorMessage(data, fallback) {
    return data?.error || data?.message || fallback;
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { graphId, version } = payload;
    const isTemplateGraph = event.payload?.template === true;

    const requestConfig = { headers, params: { version } };
    if (isTemplateGraph) {
      requestConfig.params = { ...requestConfig.params, template: true };
    }

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').get(`/graphs/${graphId}`, {
        ...requestConfig,
        headers: enhancedHeaders
      });
    }
    return this.getClient('aiadmin').get(`/graphs/${graphId}`, requestConfig);
  }

  handleResponse(response) {
    const { data } = response;
    if (data && data.success) {
      const graphModel = Framework.getStoreRegistry().getStore('graphModel');
      graphModel.clearError();
      graphModel.setGraphData(data);
      graphModel.setHistoryExists(data.historyExists || false);
      Framework.getEventManager().publish(constants.GET_GRAPH_SUCCESS, { data });
    } else {
      const msg = this.getErrorMessage(data, 'Unknown error');
      const notification = {
        open: true,
        type: 'error',
        message: `Error loading graph: ${msg}`
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      Framework.getStoreRegistry().getStore('graphModel').setError(msg);
      Framework.getEventManager().publish(constants.GET_GRAPH_ERROR);
    }
  }

  handleError(error) {
    const { response } = error;
    if (response) {
      const { status, data } = response;
      if (status === 401) {
        utils.redirectToLogin();
      }
      const msg = this.getErrorMessage(data, 'Failed to load graph. Please try again.');
      const notification = {
        open: true,
        message: msg,
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      Framework.getStoreRegistry().getStore('graphModel').setError(msg);
    } else if (error.message === 'timeout of 60000ms exceeded') {
      const notification = {
        open: true,
        message: 'Request timeout. Please try again.',
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      Framework.getStoreRegistry().getStore('graphModel').setError('Request timeout. Please try again.');
    }
    Framework.getEventManager().publish(constants.GET_GRAPH_ERROR);
  }
}
